import { useMutation, useQueryClient } from 'react-query';

import { MutationFunction, useRequest } from '@frontend/api';

import type { SavedView, SavedViewPayload } from './types';
import { getSavedViewsListQueryKey } from './utils';

export const useCreateSavedView: MutationFunction<
  SavedView,
  SavedViewPayload
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();
  const listKey = getSavedViewsListQueryKey();

  return useMutation(
    (requestData) =>
      request<SavedView>(`/display_configs/views`, {
        query,
        init: {
          method: 'POST',
          body: requestData,
        },
      }),
    {
      ...queryOptions,
      onMutate: async (vars) => {
        queryOptions?.onMutate?.(vars);

        await queryClient.cancelQueries(listKey);
        const previousViews = queryClient.getQueryData(listKey);
        queryClient.setQueryData(listKey, (old?: SavedView[]) =>
          [
            ...(old ?? []),
            {
              id: (old?.length ?? 1) * -1,
              userOwner: {},
              ...vars,
            } as SavedView,
          ].sort((a, b) => a.name.localeCompare(b.name)),
        );

        return { previousViews };
      },
      onError: (...args) => {
        queryOptions?.onError?.(...args);

        const [, , context] = args;
        queryClient.setQueryData(listKey, context?.previousViews ?? []);
      },
      onSettled: (...args) => {
        queryOptions?.onSettled?.(...args);

        queryClient.invalidateQueries({ queryKey: listKey });
      },

      mutationKey: ['createSavedView'],
    },
  );
};
